// English strings for P5-B (STORE, THEME, CONTENT) (D-013: English only). Owned by that worker; spread into `en` as
// `en.storefront`, so keys read `t("storefront.…")`.
// `shop.*` is the shopper-facing storefront chrome (merchant content is never here); `admin.*` is the
// vendor admin for the online store.
export const storefrontStrings = {
  shop: {
    skip: "Skip to main content",
    menu: "Menu",
    closeMenu: "Close menu",
    search: "Search",
    searchPlaceholder: "Search products",
    cart: "Cart",
    cartCount: { one: "Cart, {count} item", other: "Cart, {count} items" },
    account: "Account",
    home: "Home",
    viewAll: "View all",
    allProducts: "All products",
    from: "From {price}",
    save: "Save {amount}",
    percentOff: "{pct}% off",
    soldOut: "Sold out",
    lowStock: "Only a few left",
    inStock: "In stock",
    comingSoon: "Coming soon",
    priceOnRequest: "Price on request",
    noImage: "No photo yet",
    emptyListing: "No products here yet. Check back soon.",
    listingCapped: "Showing the first {count} products.",
    collectionCode: "Use code at checkout:",
    merchantQuotes: "Shared by the store",
    endsAt: "Ends {when}",
    faqTitle: "Frequently asked questions",
    branchesTitle: "Branches",
    blog: {
      title: "Blog",
      empty: "No posts yet. Check back soon.",
      readMore: "Read more",
      back: "All posts",
    },
    contactTitle: "Contact us",
    whatsapp: "Message on WhatsApp",
    call: "Call {phone}",
    email: "Email {email}",
    product: {
      reviews: {
        heading: "Customer reviews",
        write: "Write a review",
        loading: "Loading reviews…",
        none: "No reviews yet.",
        first: "Be the first to review this product.",
        unavailable: "Reviews couldn't load. Refresh the page to try again.",
        count: { one: "{count} review", other: "{count} reviews" },
        countParen: { one: "({count} review)", other: "({count} reviews)" },
        forProduct: {
          one: "({count} review for this product)",
          other: "({count} reviews for this product)",
        },
        heading2: "Reviews",
        starsRow: { one: "{count} Star", other: "{count} Stars" },
        ratingOf: "{rating} out of 5",
        verified: "Verified purchase",
        reply: "Reply from the shop",
        rating: "Your rating",
        name: "Your name",
        title: "Title (optional)",
        body: "Your review",
        held: "Your review appears once the shop approves it.",
        send: "Send review",
        sending: "Sending…",
        missing: "Add your name and your review.",
        thanks: "Thank you! Your review appears once the shop approves it.",
        errors: {
          exists: "You have already reviewed this product.",
          tooMany: "Too many reviews from this connection. Try again in a few minutes.",
          check: "Complete the check and try again.",
          failed: "Your review couldn't be sent. Try again.",
        },
      },
      breadcrumb: "Breadcrumb",
      options: "Options",
      chooseOption: "Choose {name}",
      unavailableOption: "{value} (sold out)",
      quantity: "Quantity",
      decrease: "Decrease quantity",
      increase: "Increase quantity",
      addToCart: "Add to cart",
      // Owner comment c3 (Default theme): the one button reads this before the choice is in the cart.
      addToCartTitle: "Add to Cart",
      buyNow: "Buy now",
      adding: "Adding…",
      added: "Added to your cart",
      pickVariant: "Choose an option to continue",
      unavailable: "This option is sold out",
      description: "Description",
      related: "You may also like",
      // D-114 Default theme; D-128 its product page matched to the owner's shop.
      similar: "Often Bought Together",
      checkout: "Checkout",
      skuLabel: "SKU:",
      tabs: "Product information",
      detailsTab: "Details",
      reviewTab: "Review",
      brandLabel: "Brand:",
      shippingNote: "Shipping is calculated at checkout",
      showImage: "Show picture {n}",
      prevImage: "Previous picture",
      nextImage: "Next picture",
      share: "Share",
      copyLink: "Copy link",
      linkCopied: "Link copied",
      priceNote: "Prices include tax where it applies. The final total is shown at checkout.",
      whyUs: "Why shop with us",
      stockChecking: "Checking stock",
      moreDetails: "More details",
      video: "Watch the product video",
      videoOf: "Video of {title}",
      features: "Highlights",
      specs: "Specifications",
      freeDelivery: "Free delivery",
      deliveryTime: "Delivery: {time}",
      questions: "Personalise it",
      optionalField: "(optional)",
      requiredField: "Fill this in to continue",
      fieldExtra: "+{price}",
      chooseOne: "Choose…",
      photoLater: "We'll ask you for the photo after you order.",
      installation: "Add {name}",
      installationFrom: "from {price}, priced for your address at checkout",
      installationTime: "Takes {time}",
      answersMissing: "Answer the required questions first",
      swatch: "Colour {value}",
    },
    cartPage: {
      title: "Your cart",
      empty: "Your cart is empty.",
      continue: "Continue shopping",
      remove: "Remove {title}",
      subtotal: "Subtotal",
      checkout: "Check out",
      note: "Delivery and any discount are worked out at checkout.",
      loading: "Loading your cart",
      error: "We couldn't load your cart. Check your connection and retry.",
      retry: "Retry",
      lineTotal: "Line total",
      updateError: "We couldn't update your cart. Try again.",
      unknownItem: "Item no longer listed",
      unavailable: "No longer available",
      removeUnavailable: "Some items are no longer available. Remove them to check out.",
      atCheckout: "Shown at checkout",
      // D-114 Default theme cart drawer.
      close: "Close cart",
      haveCode: "Have a code?",
      delivery: "Delivery",
      deliveryLater: "Calculated at checkout",
      total: "Total",
      checkoutCount: "Checkout ({count})",
      // D-128 cart panel tick boxes.
      selectAll: "Select All ({selected}/{total})",
      selectAllLabel: "Select all items",
      tickLine: "Buy {title} now",
    },
    checkout: {
      title: "Checkout",
      noJs: "Checkout needs JavaScript. Turn it on, or call the store to order.",
      summary: { one: "{count} item", other: "{count} items" },
      step1: "Your phone",
      phone: "Phone Number",
      phoneHelp: "We send order updates to this number.",
      // D-114 checkout layout (every theme).
      phoneAlt: "Alternate Phone Number (Optional)",
      phoneAltInvalid: "Enter a 10-digit Nepal mobile number, or leave this empty.",
      emailLabel: "Email",
      emailRequired: "Enter your email address.",
      fullAddress: "Full Address",
      orderDetail: "Order Detail",
      // Checkout redesign (owner, 2026-11-01): an "Order Summary" card.
      orderSummary: "Order Summary",
      variant: "Variant",
      payMethods: "Payment Methods",
      back: "Back to cart",
      pickPlace: "Pick a place",
      couponPlaceholder: "Promo code",
      recheck: "We check prices and offers again when you place the order.",
      placeholders: {
        name: "eg: Hari Bahadur",
        email: "eg: youmail@example.com",
        phone: "eg: 98XXXXXXXX",
        phoneAlt: "eg: 97XXXXXXXX",
        address: "eg: Tinkune, Kathmandu",
        landmark: "eg: near a school or temple",
        note: "eg: Please deliver my order carefully.",
      },
      phoneInvalid: "Enter a 10-digit Nepal mobile number, for example 985 123 4567.",
      name: "Full Name",
      nameRequired: "Enter your name.",
      email: "Email (Optional)",
      emailInvalid: "Check the email address.",
      step2: "Delivery address",
      province: "Province",
      district: "District",
      localLevel: "Municipality",
      ward: "Ward",
      tole: "Tole or street",
      landmark: "Landmark (optional)",
      landmarkHelp: "Something a courier can find, for example a school or temple.",
      choose: "Choose",
      addressRequired: "Choose your district and enter your full address.",
      checkingArea: "Checking delivery to your area",
      unserviceable: "This store doesn't deliver to this area yet. Try another address.",
      delivery: "Delivery",
      eta: "{min}–{max} days",
      step3: "Payment",
      methodsAfterAddress: "Enter your delivery address to see the ways to pay.",
      methods: {
        cod: "Cash on Delivery",
        manual_qr: "Scan a QR code (mobile banking or wallet)",
        esewa: "eSewa",
        khalti: "Khalti",
        stripe: "Card (Visa, Mastercard, Amex)",
        fonepay: "Fonepay QR",
        fonepay_qr: "Fonepay QR",
        fonepay_web: "Fonepay",
        bank_transfer: "Bank transfer",
      },
      methodUnavailable: "Not available for this order",
      codLimit: "COD is available up to {amount}.",
      coupon: "Promo code",
      apply: "Apply",
      couponInvalid: "This code can't be used on this order.",
      subtotal: "Subtotal",
      discount: "Discount",
      deliveryFee: "Delivery",
      surcharge: "Payment fee",
      tax: "Tax",
      total: "Total",
      consent: "Send me updates about this order by SMS.",
      consentRequired: "We need your permission to send order updates.",
      marketing: "Also send me offers (optional).",
      note: "My Order Note (Optional)",
      agree: "By placing the order you agree to the store's policies.",
      place: "Place Order · {total}",
      placing: "Placing your order…",
      quoteChanged:
        "Prices or delivery changed since you started. Check the new total and place the order again.",
      outOfStock: "Some items are no longer in stock. Update your cart and try again.",
      blocked: "This store can't accept this order online.",
      contactStore: "Contact the store",
      otpRequired: "This store needs to confirm your phone number before a cash-on-delivery order.",
      genericError: "We couldn't place your order. Your cart is kept. Try again.",
      unavailable: "Checkout isn't available yet for this store.",
      emptyCart: "Your cart is empty.",
      expired: "Your quote expired. We've refreshed it.",
      loading: "Loading checkout",
      cartConverted: "This cart was already ordered. Add the items again to place a new order.",
      // Settings redesign (CHECKOUT-05): landmark, terms, custom questions, account-only checkout.
      landmarkLabel: "Landmark",
      landmarkRequired: "Enter a landmark so the courier can find you.",
      terms: "I agree to the store's terms.",
      termsRequired: "Tick the box to accept the store's terms.",
      fieldOptional: "{label} (optional)",
      fieldRequired: "Answer this question.",
      fieldInvalid: "Check this answer.",
      signInRequired: "This store asks you to sign in to your account before you order.",
      signIn: "Sign in",
    },
    thanks: {
      title: "Thank you, your order is placed",
      number: "Order number {number}",
      next: "We'll send updates by SMS. Keep this number for tracking.",
      track: "Track this order",
      // D-128 thank-you page (matched to the owner's shop page).
      congrats: "Congratulations! Your order has been placed successfully!",
      lead: "Thank you for your purchase. You will receive a confirmation SMS or call soon.",
      trackTitle: "Track Your Order",
      customer: "Customer:",
      orderStatus: "Order Status:",
      summary: "Order Summary",
      items: "Cart Items",
      subtotal: "Sub-total",
      discount: "Discount",
      delivery: "Delivery Charge",
      extras: "Extras",
      total: "Total",
      reviewsTitle: "Reviews",
      reviewLater:
        "You can review this order only after it's delivered; currently it is not eligible for review",
      reviewReady: "Your order is delivered. You can review its products on their pages.",
      // D-128 review from the tracking link.
      reviewProduct: "Review Product",
      ratedOf: "{rating} out of 5",
      close: "Close",
      selected: "Selected Product",
      yourRating: "Your Rating",
      reviewLabel: "Review",
      phoneLabel: "Phone number (used to create order)",
      phoneHint: "ending with {digits}",
      submitReview: "Submit Review",
      sending: "Sending…",
      success: "Success",
      recorded: "Review recorded successfully",
      errors: {
        missing: "Write a few words about the product.",
        phone: "Enter the phone number this order was placed with.",
        exists: "You have already reviewed this product.",
        tooMany: "Too many reviews from this connection. Try again in a few minutes.",
        notReviewable: "You can review this order once it is delivered and paid.",
        failed: "Your review couldn't be sent. Try again.",
      },
      statuses: {
        draft: "Draft",
        pending: "Pending",
        confirmed: "Confirmed",
        processing: "Processing",
        ready: "Ready",
        shipped: "Shipped",
        delivered: "Delivered",
        completed: "Completed",
        cancelled: "Cancelled",
        returned: "Returned",
      },
      payments: {
        unpaid: "Unpaid",
        partial: "Partly paid",
        paid: "Paid",
        refunded: "Refunded",
        partially_refunded: "Partly refunded",
        awaiting_verification: "Checking",
        overpaid: "Paid",
      },
    },
    track: {
      title: "Track your order",
      lead: "Enter your order number and the phone number you ordered with.",
      number: "Order number",
      phone: "Phone number",
      submit: "Send code",
      notFound: "We couldn't find an order with these details.",
      status: "Status: {status}",
      loading: "Looking up your order",
      error: "We couldn't look up the order. Try again.",
      numberRequired: "Enter the order number from your confirmation.",
      phoneInvalid: "Enter a 10-digit Nepal mobile number, for example 985 123 4567.",
      codeSent:
        "If these details match an order, we sent a 6-digit code to the email address on it. The code works for 10 minutes. No email on the order? Contact the store.",
      code: "6-digit code",
      verify: "Show my order",
      startOver: "Start again",
      codeInvalid: "Enter the 6 digits from the email.",
      codeWrong: "That code doesn't match. Check the email and try again.",
      codeExpired: "That code has expired. Enter your details again for a new one.",
      locked: "Too many wrong codes. Enter your details again for a new code.",
      rateLimited: "Too many attempts. Wait a few minutes and try again.",
      unavailable: "Order lookup isn't available yet for this store. Contact the store.",
      opening: "Opening your order",
    },
    order: {
      title: "Your order",
      loading: "Loading your order",
      number: "Order {number}",
      statusLabel: "Status",
      paymentLabel: "Payment",
      placedLabel: "Placed",
      courierLabel: "Courier",
      trackingLabel: "Tracking number",
      couriers: { ncm: "Nepal Can Move", pathao: "Pathao Parcel" },
      statuses: {
        draft: "Draft",
        pending: "Received",
        confirmed: "Confirmed",
        processing: "Being prepared",
        ready: "Ready to send",
        shipped: "On the way",
        delivered: "Delivered",
        completed: "Completed",
        cancelled: "Cancelled",
        returned: "Returned",
      },
      payments: {
        unpaid: "Not paid yet",
        partial: "Partly paid",
        paid: "Paid",
        refunded: "Refunded",
        partially_refunded: "Partly refunded",
        awaiting_verification: "Payment being checked",
        overpaid: "Paid",
      },
      methods: {
        cod: "Cash on Delivery",
        manual_qr: "QR payment",
        fonepay_qr: "Fonepay QR",
        fonepay_web: "Fonepay",
        esewa: "eSewa",
        khalti: "Khalti",
        stripe: "Card (Visa, Mastercard, Amex)",
      },
      events: {
        order_created: "Order placed",
        payment_proof_submitted: "Payment screenshot sent",
        payment_verified: "Payment confirmed",
        payment_rejected: "Payment screenshot not accepted",
        payment_recorded: "Payment received",
      },
      payDue: "{amount} is still to pay.",
      payNow: "Pay now",
      items: "Items",
      subtotal: "Subtotal",
      discount: "Discount",
      delivery: "Delivery",
      surcharge: "Payment fee",
      tax: "Tax",
      total: "Total",
      paid: "Paid",
      due: "To pay",
      deliverTo: "Delivery",
      ward: "Ward {ward}",
      maskedNote: "Personal details are partly hidden to keep them private.",
      timeline: "Updates",
      cancel: "Cancel order",
      cancelConfirm: "Cancel this order? This can't be undone.",
      cancelYes: "Yes, cancel the order",
      cancelNo: "Keep my order",
      cancelling: "Cancelling your order",
      cancelled: "Your order is cancelled.",
      cancelClosed: "This order can no longer be cancelled online. Contact the store.",
      cancelError: "We couldn't cancel the order. Try again.",
      notFound:
        "We couldn't find this order. The link may have expired. Look it up with your order number and phone.",
      lookup: "Look up an order",
      error: "We couldn't load your order. Check your connection and retry.",
      unavailable: "Order tracking isn't available right now. Try again later.",
      retry: "Retry",
    },
    pay: {
      title: "Pay for your order",
      loading: "Loading payment details",
      number: "Order {number}",
      amountDue: "Amount to pay",
      step1: "1. Pay {amount} to this account",
      step2: "2. Send the payment screenshot",
      accountName: "Account name:",
      kinds: { wallet: "Wallet", bank: "Bank account", fonepay: "Fonepay", other: "QR code" },
      qrAlt: "QR code for {label}",
      qrMissing: "The QR image isn't shown here. Use the account details above.",
      file: "Payment screenshot",
      fileHelp:
        "A JPEG, PNG or WebP image. We make it smaller and remove hidden details, such as location, before sending.",
      chosen: "Selected: {name}",
      fileRequired: "Choose the screenshot of your payment.",
      reference: "Reference code (optional)",
      referenceHelp: "The transaction ID from your banking or wallet app, if it shows one.",
      send: "Send screenshot",
      sending: "Sending…",
      preparing: "Preparing your screenshot",
      sent: "Screenshot sent.",
      awaiting:
        "We received your screenshot. The store is checking the payment and will confirm it. You'll get an update by SMS.",
      paid: "This order is paid. Thank you.",
      nothingDue: "Nothing is due on this order.",
      cod: "This order is paid by cash on delivery. There is nothing to pay here.",
      cancelled: "This order was cancelled. There is nothing to pay.",
      noAccounts: "This store hasn't added a QR account yet. Contact the store to pay.",
      pendingActivation:
        "Online payment isn't active for this store yet. Contact the store to pay another way.",
      overLimit:
        "This store's online payment can't take this amount yet. Contact the store to pay another way.",
      continue: "Continue to payment",
      // Fonepay Dynamic QR (D-085).
      qrTitle: "Scan to pay {amount}",
      qrHelp:
        "Open any bank or wallet app that pays by Fonepay QR, scan this code and pay. This page updates by itself.",
      qrLeft: "The QR works for {time} more",
      qrExpired: "This QR expired. Get a new one to pay.",
      qrNew: "Get a new QR",
      qrLabel: "Payment QR code",
      verifying: "We're confirming your payment. This page updates by itself.",
      failed: "The payment wasn't completed. You can try again.",
      tryAgain: "Try again",
      stillChecking:
        "We're still confirming your payment. If you paid, this order updates once the payment is confirmed. You can check it any time from the tracking page.",
      rejected: "The store couldn't match your last screenshot. Send a clearer one.",
      rejectedReason: "The store couldn't match your last screenshot: {reason}",
      badType: "Choose a JPEG, PNG or WebP image.",
      badImage: "We couldn't read this image. Take a new screenshot and try again.",
      tooLarge: "This image is too large. Take a screenshot instead of a photo and try again.",
      alreadyPending: "A screenshot for this order is already being checked.",
      stateChanged: "This order changed. Here is its current payment state.",
      assetInvalid: "We couldn't use that upload. Choose the screenshot again.",
      uploadUnavailable:
        "Sending screenshots isn't available for this store yet. Contact the store and share it there.",
      rateLimited: "Too many attempts. Wait a few minutes and try again.",
      network: "You seem to be offline. Check your connection and try again.",
      error: "Something went wrong. Try again.",
      unavailable: "Payment isn't available right now. Try again later.",
      notFound:
        "We couldn't find this payment link. It may have expired. Look up your order with its number and phone.",
      lookup: "Look up an order",
      track: "Track this order",
      retry: "Retry",
    },
    noJs: "This page needs JavaScript. Turn it on, or contact the store.",
    accountPage: {
      title: "Your account",
      lead: "Sign in with a code sent to your email.",
      email: "Email",
      phone: "Mobile number",
      send: "Send code",
      code: "6-digit code",
      verify: "Sign in",
      orders: "Your orders",
      noOrders: "No orders yet.",
      signOut: "Sign out",
      error: "We couldn't sign you in. Check the code and try again.",
      unavailable: "Accounts aren't available for this store yet.",
      phoneTitle: "Add your mobile number",
      phoneLead: "We'll text you a code. Orders you placed with this number then show up here.",
      phoneVerify: "Confirm number",
      phoneDone: "Mobile number {phone} confirmed.",
      phoneError: "We couldn't confirm that number. Check the code and try again.",
      phoneTaken: "That number is already on another account.",
      phoneUnavailable: "This store can't send text codes yet.",
    },
    // CONV-001 (D-055): the website chat bubble. Replies show up in the shop's Sampark.
    chat: {
      open: "Chat with us",
      close: "Close chat",
      online: "We reply here. You can leave this page open.",
      name: "Your name",
      phone: "Phone number",
      phoneOptional: "Phone number (optional)",
      placeholder: "Write a message…",
      send: "Send",
      start: "Start chat",
      you: "You",
      error: "We couldn't send that. Try again.",
      tooMany: "Too many messages. Wait a minute and try again.",
      check: "Complete the check above, then send.",
      sending: "Sending…",
      viewProduct: "View",
    },
    // LOYALTY-001: points on the account page and the checkout "use points" control.
    loyalty: {
      balance: { one: "{count} point", other: "{count} points" },
      worth: "Worth {amount} at checkout",
      memberNo: "Member number {number}",
      level: "Level: {name}",
      nextLevel: "Spend {amount} more to reach {name}",
      earnRate: {
        one: "Earn {count} point for every {amount} you spend.",
        other: "Earn {count} points for every {amount} you spend.",
      },
      expiry: "Points expire {days} days after you earn them.",
      notMember: "You'll start earning points with your first paid order.",
      rewards: "Rewards",
      rewardCost: { one: "{count} point", other: "{count} points" },
      perk: "{name}: {perk}",
      history: "Recent points",
      kinds: {
        earn: "Earned",
        welcome: "Welcome bonus",
        redeem: "Used",
        reverse: "Returned or taken back",
        expire: "Expired",
        adjust: "Adjusted by the store",
      },
      forOrder: "Order {number}",
      use: "Use my points",
      applied: "{points} used, {amount} off",
      minimum: {
        one: "Use at least {count} point at a time.",
        other: "Use at least {count} points at a time.",
      },
      cannotUse: "Points can't be used on this order.",
      changed: "Your points balance changed. Review your order again.",
    },
    searchPage: {
      title: "Search",
      results: { one: "{count} result for “{q}”", other: "{count} results for “{q}”" },
      none: "No products match “{q}”.",
      unavailable: "Search isn't available yet. Browse all products instead.",
    },
    forms: {
      name: "Your name",
      phone: "Mobile number",
      email: "Email (optional)",
      message: "Message",
      orderNumber: "Order number (optional)",
      complaint: "Your complaint",
      send: "Send",
      sending: "Sending…",
      contactLead: "Send us a message and we'll reply by phone or email.",
      contactSent: "Message sent. Your reference is {reference}.",
      grievanceTitle: "Make a complaint",
      grievanceLead: "Tell us what went wrong. You get a reference number straight away.",
      grievanceSent: "Complaint received. Reference {reference}. The store will respond by {due}.",
      invalid: "Check the highlighted fields.",
      rateLimited: "Too many messages from this connection. Try again in a few minutes.",
      error: "We couldn't send this. Try again.",
      required: "Required",
    },
    // D-067 (RISK-001): a shopper's report to Adyvance about this shop.
    report: {
      title: "Report this shop to Adyvance",
      lead: "Adyvance runs the platform this shop uses. Tell us if something is wrong with this shop. The shop does not see who sent a report.",
      reason: "What is wrong",
      reasons: {
        scam: "It looks like a scam",
        counterfeit: "Fake or counterfeit products",
        prohibited: "Illegal or prohibited items",
        not_delivered: "I paid and got nothing",
        abusive: "Abusive or offensive content",
        other: "Something else",
      },
      details: "What happened",
      detailsHelp: "At least 10 characters. Add the order number if you have one.",
      contact: "Your email or phone (optional)",
      contactHelp: "Only if you want Adyvance to contact you.",
      send: "Send report",
      complaintNote: "A problem with your own order? The shop must answer a complaint first:",
      sentTitle: "Report sent",
      sentBody: "Thank you. Your reference is {number}. Adyvance reviews every report.",
      failedTitle: "We could not send your report",
      failedBody:
        "Too many reports were sent from here, or the check did not pass. Try again later.",
      back: "Back to the shop",
    },
    legal: {
      blockTitle: "Business information",
      registeredName: "Registered name",
      businessType: "Business type",
      panVat: "Permanent account number (tax registration)",
      registration: "Registration number",
      registrationBody: "Registered with",
      ecommerce: "E-commerce listing number",
      complaintOfficer: "Complaint officer",
      address: "Address",
      contact: "Contact",
      branches: "Branches",
      notProvided: "Not provided yet",
      fullPage: "Full business information",
      grievance: "Make a complaint",
      reportShop: "Report this shop",
      businessTypes: {
        sole_proprietorship: "Sole proprietorship",
        partnership: "Partnership",
        private_limited: "Private limited company",
        public_limited: "Public limited company",
        cooperative: "Cooperative",
        other: "Other",
      },
    },
    footer: {
      policies: "Policies",
      help: "Help",
      payments: "Ways to pay",
      poweredBy: "Online store by Adyvance",
      /** D-115 Default footer: "Made with Adyvance" (the brand name is linked, not translated). */
      madeWith: "Made with",
      rights: "All rights reserved.",
      followUs: "Follow Us",
      track: "Track your order",
      contact: "Contact",
      contactUs: "Contact Us",
      legal: "Business information",
      social: "Follow us",
      socialNetworks: {
        facebook: "Facebook",
        instagram: "Instagram",
        tiktok: "TikTok",
        youtube: "YouTube",
        x: "X",
      },
    },
    gate: {
      comingSoonTitle: "Opening soon",
      comingSoonBody: "This store is getting ready. Check back soon.",
      passwordTitle: "This store is private for now",
      passwordBody: "Enter the store password to look around.",
      password: "Password",
      enter: "Enter",
      wrongPassword: "That password didn't work.",
      maintenanceTitle: "This store is unavailable",
      maintenanceBody: "The store is not taking orders right now. Please try again later.",
      previewBanner: "Preview: shoppers can't see this version yet.",
    },
    notFound: {
      title: "We couldn't find this page",
      body: "The link may be old, or the item is no longer sold here.",
      home: "Go to the home page",
      all: "Browse all products",
    },
    storeNotFound: {
      title: "Store not found",
      body: "There is no store at this address.",
    },
    badges: {
      cod: "COD",
      delivery: "Delivery",
      returns: "Returns",
      secure: "Secure checkout",
      support: "Support",
      local: "Made in Nepal",
    },
    // D-091 slides and the one-at-a-time customer quotes (islands/slider.ts).
    slider: {
      label: "Featured offers",
      quotes: "Customer quotes",
      prev: "Previous slide",
      next: "Next slide",
      goTo: "Go to slide {n}",
      slideOf: "{n} of {total}",
      carousel: "carousel",
      slide: "slide",
    },
    // D-091 mega menu under main-menu links that have sub-links.
    mega: { categories: "Categories", viewMore: "View more" },
    // D-091 email sign-up section (CUSTOMER-04 double opt-in, islands/newsletter.ts).
    newsletter: {
      email: "Email address",
      placeholder: "Your email address",
      subscribe: "Subscribe",
      consent: "Email me news and offers from this shop. I can unsubscribe at any time.",
      ok: "Almost done: check your inbox and confirm your email.",
      later: "We couldn't send the confirmation email right now. Please try again later.",
      invalid: "Enter a valid email address.",
      needConsent: "Tick the box to agree to receive emails from this shop.",
      check: "Complete the check and try again.",
      limited: "Too many tries. Wait a moment and try again.",
      error: "Something went wrong. Please try again.",
    },
    // D-092 signature-theme sections (routine, before/after, compare, shop the look, product tabs).
    routine: { step: "Step {n}", shop: "Shop now" },
    beforeAfter: {
      before: "Before",
      after: "After",
      handle: "Slide to compare before and after",
    },
    compare: { caption: "Compare products", feature: "Feature", view: "View" },
    look: {
      dot: "Show piece {n}: {title}",
      list: "Pieces in this look",
      view: "Show product",
      close: "Close",
    },
    tabs: { label: "Product groups" },
    quick: { view: "View" },
    // PRODUCT-06 digital products on the thanks and order pages (islands/digital.ts).
    digital: {
      title: "Your downloads",
      awaitingPayment: "Your downloads appear here once your payment is confirmed.",
      preparing: "We're preparing your downloads. This page updates in a moment.",
      download: "Download",
      openLink: "Open link",
      keyLabel: "Licence key",
      keyWaiting: "Your licence key will appear here soon. Check this page again later.",
      copy: "Copy",
      copied: "Copied",
      uses: "{used} of {limit} downloads used",
      expires: "available until {date}",
      expired: "This download has expired. Contact the store for a new link.",
      limitReached: "All downloads have been used. Contact the store for a new link.",
      error: "We couldn't load your downloads. Refresh the page to try again.",
    },
  },
  admin: {
    loadFailed: "We couldn't load this. Check your connection and retry.",
    saved: "Saved",
    saving: "Saving…",
    saveFailed: "Couldn't save. Your changes are kept here; retry.",
    conflict: "Someone else saved a newer version. Reload to see it before you edit.",
    reload: "Reload",
    save: "Save",
    add: "Add",
    remove: "Remove",
    moveUp: "Move up",
    moveDown: "Move down",
    edit: "Edit",
    cancel: "Cancel",
    empty: "Nothing here yet",
    live: "Live",
    draft: "Draft",
    reviewRequired: "Review required",
    published: "Published",
    notPublished: "Not published",
    theme: {
      title: "Theme and editor",
      lead: "Edit your store's look. Changes save as a draft; shoppers see them only after you publish.",
      /** The Templates page (owner, 2026-11-01). */
      templatesLead:
        "Pick the look of your store. Your products, pages and settings stay as they are; shoppers see a new look only after you publish it.",
      publish: "Publish",
      publishing: "Publishing…",
      publishedToast: "Published. Your store shows the new version within about a minute.",
      publishTitle: "Publish this draft?",
      publishBody:
        "Shoppers will see these changes within about a minute. You can restore an earlier version at any time.",
      versionLabel: "Version name (optional)",
      preview: "Preview",
      previewFailed: "Couldn't create a preview link.",
      previewReady: "Preview link ready. It works for one hour.",
      draftSaved: "Draft saved {when}",
      unpublished: "Unpublished changes",
      upToDate: "Live version is up to date",
      liveVersion: "Live: version {n}",
      /** Published before, but another theme is on the shop now. */
      notLive: "Not live",
      neverPublished: "Not published yet",
      studio: {
        title: "Theme editor",
        back: "Back to admin",
        focus: "Focus mode: the preview alone (Ctrl+B)",
        versions: "Versions: published versions to restore",
        brand: "Branding, header and footer: logo, favicon, colours, fonts, header and footer",
        keys: "Keyboard shortcuts",
        openStore: "Open your store",
        keyList: {
          save: "Save the draft now",
          undo: "Undo",
          redo: "Redo",
          focus: "Show or hide the side panel",
          list: "Show this list",
        },
        keysNote:
          "On a Mac use ⌘ instead of Ctrl. Changes also save by themselves a moment after you stop.",
      },
      tabs: {
        home: "Home page",
        brand: "Branding",
        chrome: "Header and footer",
        look: "Branding, header and footer",
        themes: "Themes",
        versions: "Versions",
      },
      sectionsLabel: "Theme sections",
      pickTitle: "Pick a theme to start",
      pickBody:
        "Pick a free theme, or a premium one from your Adyvance Wallet. You can switch later without losing your products.",
      install: "Apply",
      installing: "Applying…",
      current: "Current theme",
      switchNote: "Switching creates a separate draft. Publish it to make it live.",
      brand: {
        primary: "Main colour",
        primaryHelp: "Used for buttons and links. Text on it is white.",
        accent: "Accent colour",
        accentHelp: "Used for badges and small highlights.",
        contrastFail:
          "Too light for white text (contrast {ratio}:1, needs 4.5:1). Pick a darker shade.",
        contrastOk: "Readable: contrast {ratio}:1",
        identity: "Store name, logo and icon",
        identityHelp: "Edited in your store profile, shared by the store and invoices.",
        identityLink: "Open store profile",
        radius: "Corner style",
        radiusSm: "Square",
        radiusMd: "Rounded",
        radiusLg: "Soft",
        currency: "Price label",
        ratio: "Product photo shape",
        ratioSquare: "Square",
        ratioPortrait: "Tall",
        ratioLandscape: "Wide",
      },
      chrome: {
        announcement: "Announcement bar",
        announcementHelp: "A short line above the header, for example a delivery offer.",
        showSearch: "Show search in the header",
        showSearchShort: "Show",
        shown: "Shown",
        hidden: "Hidden",
        footerNote: "Footer note",
      },
      versions: {
        empty: "No versions yet. Publish to create the first one.",
        number: "Version {n}",
        restore: "Restore",
        restoreTitle: "Restore version {n}?",
        restoreBody:
          "This publishes version {n} again as a new version. The current one stays in the history.",
        restored: "Restored. Your store shows it within about a minute.",
        name: "Name",
        nameTitle: "Name version {n}",
        named: "Named versions are kept until you delete them.",
        publishedAt: "Published {when}",
        liveBadge: "Live",
      },
    },
    preview: {
      frameTitle: "Live preview of your store",
      empty: "This page has no sections yet. Add one from the panel on the left.",
      headingHint: "Your heading",
      bodyHint: "Your text appears here.",
    },
    pageBar: {
      pick: "Pages",
      search: "Search pages…",
      core: "Layout and home",
      layout: "Header, footer and branding",
      layoutSub: "On every page",
      home: "Home page",
      mine: "Your pages",
      groups: { shop: "Shop", orders: "Cart and orders", content: "Content" },
      products: "Products",
      productSub: "Product page with this product",
      none: "No page matches.",
      newPage: "New page",
      view: "View this page on your store",
      editorTitle: "Page editor",
    },
    templates: {
      names: {
        product: "Product page",
        listing: "Product lists",
        search: "Search results",
        cart: "Cart",
        account: "Account",
        track: "Track an order",
        thanks: "Order placed",
        blog: "Blog",
        blog_post: "Blog post",
        contact: "Contact",
        not_found: "Page not found",
      },
      what: {
        product: "photos, price, options, Add to cart, details and reviews",
        listing: "the products of all products, a collection, category or brand, with filters",
        search: "the search box and results",
        cart: "the shopper's cart and the Check out button",
        account: "sign-in and the shopper's orders",
        track: "the order tracking form and status",
        thanks: "the order number and summary after an order",
        blog: "your posts",
        blog_post: "the post's photo and text",
        contact: "the contact form and your contact details",
        not_found: "the message for an address that doesn't exist",
      },
      mainBody:
        "Page content is the page's own part: {what}. Add sections above or below it, and move it up or down.",
      mainNote: "It can't be hidden or removed. Its look follows your branding.",
      sampleProduct: "Your product",
      productNote: "Photos, options, delivery and reviews show here on your store.",
      orderNumber: "Order number",
      thanksNote: "The order number and summary show here.",
      samplePost: "Blog post {n}",
      postNote: "Your post's photo and text show here.",
      yourName: "Your name",
      notFoundNote: "Shoppers land here when an address doesn't exist.",
    },
    links: {
      none: "No link",
      store: "Store pages",
      collections: "Collections",
      yourPages: "Your pages",
      custom: "Custom link…",
      customLabel: "Custom link",
      pages: {
        home: "Home page",
        products: "All products",
        search: "Search",
        cart: "Cart",
        account: "Account",
        track: "Track an order",
        blog: "Blog",
        faq: "Questions",
        contact: "Contact",
        branches: "Branches",
      },
    },
    editor: {
      structure: "Page structure",
      properties: "Properties",
      editing: "Editing",
      copy: "Copy (paste on any page)",
      copied: "Section copied. Paste it on this or any other page.",
      copyFailed: "This browser could not keep the copy.",
      paste: "Paste",
      pasteHelp: "Paste the copied section ({type})",
      deselect: "Done (Esc)",
      canvasTools: "Section tools",
      startTitle: "Start building your page",
      startBody: "Add your first section: a banner, products, photos or text.",
      tabs: { content: "Content", look: "Appearance", tabSource: "Products in this tab" },
      noneTitle: "No section selected",
      noneBody: "Click a section in the preview or in the list to edit it.",
      checks: {
        title: "Page check",
        score: "{done} of {total}",
        banner: "A banner at the top",
        products: "Products on the page",
        trust: "Reasons to trust you",
        contact: "A way to reach you",
        add: "Add",
      },
      tips: {
        title: "Tips",
        click: "Click any part of the preview to edit it here.",
        between: "Point between two sections in the preview and press Add section.",
        copy: "Copy a section (Ctrl+C) and paste it on any page (Ctrl+V).",
        esc: "Esc lets go of the selected section.",
      },
      gallery: {
        lead: "Every preview uses your own colours and products.",
        search: "Search sections…",
        groups: "Section groups",
        none: "No section matches \u201c{q}\u201d.",
        count: "{n} sections",
        copiedTitle: "Copied: {type}",
        copiedBody: "The section you copied, with its text and photos.",
        keys: { move: "Move", add: "Add", close: "Close", search: "Search" },
      },
      back: "Back to sections",
      show: "Show on the store",
      hide: "Hide on the store",
      hiddenNote: "Hidden: shoppers don't see this section.",
      sections: "Sections",
      drag: "Drag to move",
      addSection: "Add section",
      addBelow: "Add a section below",
      duplicate: "Duplicate",
      menuFor: "More for {name}",
      full: "A page holds up to {n} sections.",
      live: "Live preview",
      undo: "Undo (Ctrl+Z)",
      redo: "Redo (Ctrl+Y)",
      device: "Preview size",
      devices: { desktop: "Computer", tablet: "Tablet", phone: "Phone" },
      galleryTitle: "Add a section",
      presets: {
        title: "Ready product sections",
        allSections: "All sections",
        headings: {
          best_sellers: "Best sellers",
          latest: "New arrivals",
          on_sale: "On sale",
          products: "Our picks",
        },
      },
      groups: {
        all: "All",
        ready: "Ready product sections",
        copied: "Copied",
        banners: "Banners",
        products: "Products",
        content: "Text",
        media: "Photos",
        trust: "Trust",
      },
      about: {
        hero: "A big welcome with a heading, a photo and a button.",
        product_grid: "Newest, best-selling, on-sale or hand-picked products.",
        collection_list: "Tiles that open your collections.",
        rich_text: "A heading and a few paragraphs.",
        image_with_text: "A photo beside a short story and a button.",
        testimonials: "Kind words from your customers.",
        faq: "Answers to the questions shoppers ask most.",
        trust_badges: "Cash on delivery, returns, delivery and more.",
        countdown: "A sale or launch with its end time.",
        selling_points: "Short reasons to buy from you.",
        contact_cta: "WhatsApp and call buttons.",
        announcement_band: "A coloured strip with a short message.",
        marquee: "Short messages that scroll across the page.",
        logo_list: "The brands you sell, partners or press.",
        steps: "Numbered steps, like order, pay and get it delivered.",
        image_grid: "Photo tiles that link to your collections or pages.",
        hero_slider: "Big photo slides with a heading, key facts and a button.",
        product_spotlight: "One product in the middle, its strong points around it.",
        promo_banner: "A sale or offer box with a button.",
        newsletter: "Let shoppers sign up for your emails.",
        routine: "A routine or recipe in numbered steps, each with a photo and a link.",
        before_after: "Two photos with a handle to drag between them.",
        spec_numbers: "Big numbers with a unit, like 40 h battery or 2 yr warranty.",
        compare_table: "Two or three products side by side, one row per feature.",
        shop_the_look: "A photo with numbered dots that show each piece and its price.",
        tabbed_products: "Product rows behind tabs, like New, Best sellers or Under Rs 999.",
      },
      chars: "{n}/{max}",
      hrefHelp: "A page on your store like /products, or a full https:// link.",
      invalid: "Check this field.",
      items: {
        faq: "Questions",
        testimonials: "Quotes",
        trust_badges: "Badges",
        selling_points: "Points",
        collection_list: "Collections",
        marquee: "Messages",
        logo_list: "Logos",
        steps: "Steps",
        image_grid: "Tiles",
        hero_slider: "Slides",
        product_spotlight: "Strong points",
        routine: "Steps",
        spec_numbers: "Numbers",
        compare_table: "Products to compare",
        compare_table_rows: "Rows",
        shop_the_look: "Dots on the photo",
        tabbed_products: "Tabs",
      },
      // D-092 compare table and product tabs.
      compare: {
        hint: "Each row has one value per product, in the same order.",
        valueN: "Value for product {n}",
      },
      spots: { hint: "Place each dot on the piece: across and down, in percent of the photo." },
      itemN: "Item {n}",
      facts: {
        add: "Add a fact",
        remove: "Remove fact {n}",
        hint: "Up to 4 short facts, like Delivery · All over Nepal.",
      },
      noStars: "No stars",
      starsN: { one: "{count} star", other: "{count} stars" },
      source: {
        latest: "Newest products",
        latestHelp: "The products you added last.",
        best_sellers: "Best sellers",
        best_sellersHelp: "Your top sellers of the last 30 days.",
        on_sale: "On sale",
        on_saleHelp: "Products with a lower price than before.",
        collection: "A collection",
        collectionHelp: "Every product in one collection.",
        products: "Hand-picked",
        productsHelp: "Exactly the products you choose, in your order.",
        under: "Under a price",
        underHelp: "Everything at or below a price you set, newest first.",
        maxPrice: "Highest price (Rs)",
        pickCollection: "Collection",
        chooseCollection: "Choose a collection",
        pickProducts: "Choose products",
        changeProducts: "{n} picked · change",
        useProducts: "Use {n} products",
      },
      image: {
        library: "Library",
        upload: "Upload",
        uploading: "Uploading…",
        alt: "Describe the photo (for screen readers)",
        hint: "JPG, PNG or WebP. Wide photos look best.",
        rejected: {
          type: "Use a JPG, PNG, WebP or AVIF photo.",
          size: "This photo is too large. Use one under 20 MB.",
        },
      },
      brand: {
        palettes: "Ready palettes",
        palette: {
          himal: "Himal",
          dhaka: "Dhaka",
          slate: "Slate",
          plum: "Plum",
          forest: "Forest",
          ocean: "Ocean",
          saffron: "Saffron",
          night: "Night",
        },
        colours: "Colours",
        hex: "{name} code",
        identity: "Logo, icon and name",
        logo: "Logo",
        favicon: "Favicon",
        faviconHint:
          "Square image, 512 × 512 px or larger. Shows in browser tabs, bookmarks and search results. Empty: your logo is used.",
        storeName: "Store name",
        change: "Change",
        style: "Style",
      },
      chrome: {
        header: "Header",
        footer: "Footer",
        searchHint: "A search icon next to the cart.",
        noLinks: "No links yet",
        editMenus: "Edit menus",
        mega: {
          title: "Mega menu",
          toggle: "Show a big menu",
          toggleHint:
            "On a computer, pointing at a main-menu link with sub-links opens a wide panel with them and an offer photo.",
          heading: "Column title",
          headingHint: "Empty shows “Categories”.",
          promoImage: "Offer photo",
          promoImageHint: "A wide photo looks best (about 16 × 7).",
          promoHref: "Offer link",
          noChildren: "None of your main-menu links has sub-links yet. Add some in Menus.",
        },
        ideas: {
          delivery: "Free delivery inside the valley above Rs 2,000",
          cod: "Cash on delivery all over Nepal",
          sale: "Festival sale is live: up to 30% off",
        },
      },
      library: {
        current: "Current theme",
        upToDate: "All sections are up to date",
        customize: "Customize",
        filter: "Show themes",
        filters: { all: "All", free: "Free", installed: "Installed" },
        search: "Search themes",
        noMatch: "No themes match",
        noMatchBody: "Try another word or show all themes.",
        free: "Free",
        layouts: {
          mega: "Mega menu layout",
          trend: "Trend layout",
          shadow: "Shadow layout",
          elegant: "Elegant layout",
          classic: "Minimal layout",
          lumen: "Beauty layout",
          volt: "Tech layout",
          folio: "Editorial layout",
          pop: "Playful layout",
          pantry: "Grocery layout",
          default: "Simple shop layout",
        },
        preview: "Preview theme",
        previewOf: "Preview {name}",
        device: "Preview on",
        devices: { desktop: "Computer", tablet: "Tablet", phone: "Phone" },
        fullTitle: "{name} preview",
        previewMode: "Preview mode",
        closePreview: "Close preview",
        // Owner, 2026-11-01: one click puts the theme live (no draft, customise and publish).
        useTheme: "Apply",
        applied: "{name} is live on your shop",
        undo: "Undo",
        undone: "Your previous look is back",
      },
      versions: {
        emptyTitle: "No versions yet",
      },
    },
    pages: {
      lead: "Design every page of your store with a live preview, then publish it.",
      emptyTitle: "No pages yet",
      emptyBody: "Make a sale page, your brand story or a questions page from a ready layout.",
      pick: "Page to edit",
      homeOption: "Home page (theme editor)",
      unsaved: "Unsaved changes",
      savedWhen: "Saved {when}",
      settings: "Page settings",
      slugFixed: "The address is set when the page is made.",
      searchPreview: "On Google",
      noDescription: "Add a short description so shoppers know what this page is about.",
      apply: "Apply",
      titleNeeded: "Give the page a title.",
      creating: "Making the page…",
      templates: {
        landing: "Landing",
        landingHelp: "A big banner and your products.",
        sale: "Sale",
        saleHelp: "Banner, countdown, on-sale products, badges.",
        story: "Brand story",
        storyHelp: "Photo and story, quotes, contact.",
        faq: "Questions",
        faqHelp: "Questions and answers, then contact.",
        about: "About us",
        aboutHelp: "Who you are and why shop with you.",
        blank: "Blank",
        blankHelp: "Start with nothing and add sections.",
      },
      layouts: {
        saleSub: "Our best prices of the season, for a few days only.",
        saleCta: "Shop the sale",
        saleEnds: "Hurry, the sale ends soon",
        saleGrid: "On sale now",
      },
    },
    sections: {
      add: "Add a section",
      empty: "No sections yet. Add one to build the page.",
      hidden: "Hidden",
      hide: "Hide on the store",
      itemAdd: "Add item",
      itemRemove: "Remove item {n}",
      sectionLabel: "Section {n}: {type}",
      types: {
        hero: "Banner",
        product_grid: "Product grid",
        collection_list: "Collection list",
        rich_text: "Text",
        image_with_text: "Image with text",
        testimonials: "Customer quotes",
        faq: "Questions and answers",
        trust_badges: "Trust badges",
        countdown: "Countdown",
        selling_points: "Selling points",
        contact_cta: "Contact buttons",
        announcement_band: "Announcement band",
        marquee: "Scrolling text",
        logo_list: "Logos",
        steps: "How it works",
        image_grid: "Photo grid",
        hero_slider: "Slideshow",
        product_spotlight: "Product spotlight",
        promo_banner: "Offer banner",
        newsletter: "Email sign-up",
        routine: "Routine steps",
        before_after: "Before and after",
        spec_numbers: "Big numbers",
        compare_table: "Compare table",
        shop_the_look: "Shop the look",
        tabbed_products: "Product tabs",
        main: "Page content",
      },
      fields: {
        heading: "Heading",
        subheading: "Subheading",
        body: "Text",
        cta_label: "Button text",
        cta_href: "Button link",
        align: "Alignment",
        tone: "Background",
        source: "Products to show",
        sourceLatest: "Newest products",
        sourceCollection: "A collection",
        slug: "Collection address",
        limit: "How many",
        show_view_all: "Show a View all link",
        image_side: "Image side",
        ends_at: "Ends at",
        text: "Text",
        href: "Link",
        quote: "Quote",
        name: "Name",
        place: "Place",
        q: "Question",
        a: "Answer",
        icon: "Icon",
        label: "Label",
        title: "Title",
        show_whatsapp: "Show WhatsApp",
        show_phone: "Show phone",
        start: "Start",
        center: "Centre",
        end: "End",
        brand: "Brand colour",
        surface: "Plain",
        image: "Photo",
        speed: "Speed",
        slow: "Slow",
        normal: "Normal",
        fast: "Fast",
        layout: "Layout",
        grid: "Even grid",
        bento: "Big first photo",
        // D-091 fields and choices
        kicker: "Small label",
        cta2_label: "Second link text",
        cta2_href: "Second link",
        badge: "Round sticker",
        slides: "Slides",
        stats: "Key facts",
        value: "Value",
        autoplay: "Change slides by themselves",
        height: "Height",
        medium: "Medium",
        tall: "Tall",
        style: "Style",
        styleCards: "Side by side",
        styleQuote: "One at a time",
        rating: "Stars",
        features: "Strong points",
        button_label: "Button text",
        soft: "Soft",
        dark: "Dark",
        // D-092 fields and choices
        light: "Light",
        list: "Quick list",
        steps: "Steps",
        link_label: "Link text",
        before_image: "Before photo",
        before_label: "Before label",
        after_image: "After photo",
        after_label: "After label",
        unit: "Unit",
        columns: "Products",
        rows: "Rows",
        price: "Price",
        values: "Values",
        x: "Across (%)",
        y: "Down (%)",
        spots: "Dots",
        tabs: "Tabs",
      },
      quotesNote: "Quotes are shown as shared by the store, never as verified reviews.",
      invalid: "Check this section: {field}",
    },
    celebrate: {
      title: "Your shop is live!",
      body: "Customers can visit it right now. It wears the Default theme; change the look any time in Templates.",
      copy: "Copy shop address",
      view: "View my shop",
      addProduct: "Add first product",
      close: "Close",
    },
    settings: {
      title: "Store profile and legal details",
      lead: "Your public business details, store status and launch checklist.",
      statusTitle: "Store status",
      status: {
        draft: "Not published",
        coming_soon: "Coming soon",
        password: "Password protected",
        live: "Live",
        suspended: "Suspended",
      },
      statusHelp: {
        coming_soon: "Visitors see your coming-soon message. Search engines don't index the store.",
        password: "Only people with the password can look around.",
        live: "Anyone can shop.",
      },
      setStatus: "Update status",
      password: "Store password",
      passwordHelp: "At least 4 characters. Share it with people you want to let in.",
      statusSaved: "Store status updated.",
      legalWarning:
        "Your business details are incomplete. The store still works, but the law expects them on your site.",
      address: "Store address",
      links: "Your store",
      openStore: "Open store",
      previewLink: "Get a preview link",
      readinessTitle: "Launch checklist",
      readinessProgress: "{done} of {total} done",
      readiness: {
        products: "Add products",
        payment: "Set up payments",
        delivery: "Set delivery areas and fees",
        legal: "Add your business details",
        policies: "Review your policies",
        theme: "Publish your theme",
        live: "Open your store",
        first_order: "Get your first order",
      },
      readinessState: { done: "Done", todo: "To do", unknown: "Not checked yet" },
      fix: "Fix",
      legalTitle: "Business details (E-Commerce Act disclosure)",
      legalLead: "Shown in your store's footer and on its business information page.",
      completeness: "{percent}% complete",
      missingList: "Still missing: {items}",
      legalSaved: "Business details saved.",
      contactTitle: "Contact for shoppers",
      contactPhone: "Phone",
      contactWhatsapp: "WhatsApp number",
      contactEmail: "Email",
      seoDescription: "Store description for search engines",
      gateTitle: "Coming-soon message title",
      gateBody: "Coming-soon message",
      settingsSaved: "Settings saved.",
      legalFields: {
        registered_name: "Registered business name",
        business_type: "Business type",
        pan_vat: "Permanent account number (9 digits)",
        registration_no: "Registration number",
        registration_body: "Registered with",
        ecommerce_listing_no: "E-commerce listing number",
        complaint_officer_name: "Complaint officer",
        complaint_officer_contact: "Complaint officer contact",
        contact_phone: "Business phone",
        contact_email: "Business email",
        address: "Address",
        contact: "Phone or email",
        province: "Province",
        district: "District",
        local_level: "Municipality",
        ward: "Ward",
        tole: "Tole or street",
      },
      panInvalid: "Enter 9 digits.",
    },
    policies: {
      title: "Policies and legal pages",
      lead: "Policies appear in your store only after you replace every placeholder and mark them reviewed.",
      install: "Add policy templates",
      installed: "Templates added. Review each one before it goes live.",
      empty: "No policies yet. Start from our templates and adapt them to your business.",
      templateNote: "Templates are a starting point, not legal advice.",
      placeholders: "Replace: {list}",
      reviewed: "I've reviewed this policy",
      reviewedHelp: "It goes live on your store when saved.",
      savedLive: "Policy saved and live.",
      savedDraft: "Policy saved. It stays hidden until you mark it reviewed.",
      placeholdersLeft: "Replace every placeholder in double braces first.",
      fieldTitle: "Title",
      fieldBody: "Policy text",
      emptyTitle: "No policies yet",
      tiles: {
        live: "Live on the store",
        liveHint: "Shoppers can read them",
        review: "Need your review",
        reviewHint: "Hidden until you mark them reviewed",
        blanks: "Blanks to fill",
        blanksHint: "Like your phone and return days",
      },
      liveAt: "Live at {path}",
      blanksLeft: { one: "{count} blank to fill", other: "{count} blanks to fill" },
      readyToReview: "Ready for your review",
      blanksTitle: "Blanks in this policy",
      allFilled: "All filled",
      noBlanks: "Every blank is filled. Read it once more, then mark it reviewed.",
      fill: "Fill in",
      fillAll: "Fill in all from my business profile",
      saveLive: "Save and put live",
      saveDraft: "Save draft",
      setup: {
        open: "Set up all policies",
        title: "Set up all your policies",
        bannerTitle: {
          one: "{count} policy is waiting for you",
          other: "{count} policies are waiting for you",
        },
        bannerBody:
          "Answer a few questions once, read each policy, and publish them together. About two minutes.",
        step1: "Your details",
        step2: "Read and publish",
        step1Body: {
          one: "Each answer fills every place it's needed in your {count} policy.",
          other: "Each answer fills every place it's needed in your {count} policies.",
        },
        groups: {
          business: "Your business",
          delivery: "Delivery",
          returns: "Returns and refunds",
          other: "Other details",
        },
        usedIn: { one: "Used in {count} policy", other: "Used in {count} policies" },
        prefilled:
          "We filled what we could from your business profile and store settings. Change anything that isn't right.",
        missing: {
          one: "{count} answer is empty; those policies stay hidden until it's filled.",
          other: "{count} answers are empty; those policies stay hidden until they're filled.",
        },
        next: "Next: read your policies",
        step2Body:
          "Open each policy and read it. Tick the ones that match how your store works; they go live when you publish.",
        readIt: "I've read it",
        readAll: {
          one: "Mark the {count} complete policy as read",
          other: "Mark all {count} complete policies as read",
        },
        legal:
          "Templates are a starting point, not legal advice. You're responsible for what your policies say.",
        back: "Back",
        publish: { one: "Publish {count} policy", other: "Publish {count} policies" },
        saveFilled: "Save answers for later",
        done: { one: "{count} policy is now live.", other: "{count} policies are now live." },
        savedOnly: "Answers saved. Tick a policy as read to publish it.",
      },
      blanks: {
        store_name: "Business name",
        contact_phone: "Phone number",
        contact_email: "Email",
        complaint_officer: "Complaint officer",
        valley_days: "Days inside the valley",
        outside_days: "Days outside the valley",
        return_days: "Days to return",
        refund_days: "Days to refund",
      },
    },
    content: {
      title: "Pages",
      lead: "About, questions and answers, and your own pages. Landing pages can become your home page.",
      newPage: "New page",
      addPage: "Add page",
      searchLabel: "Search pages",
      searchHint: "Search pages",
      sectionCount: { one: "{count} section", other: "{count} sections" },
      pageTitle: "Page title",
      pageSlug: "Page address",
      pageSlugHelp: "Lowercase letters, numbers and hyphens, for example tihar-offers.",
      template: "Start from",
      templates: { blank: "Blank page", landing: "Landing page", about: "About page" },
      create: "Create page",
      created: "Page created.",
      empty: "No pages yet.",
      publish: "Publish page",
      publishedToast: "Page published.",
      setHome: "Make home page",
      homeBadge: "Home page",
      homeToast: "This page is now your home page.",
      unpublishedChanges: "Unpublished changes",
      aboutTitle: "About your store",
      aboutBody: "About text",
      faqTitle: "Questions and answers",
      faqQuestion: "Question",
      faqAnswer: "Answer",
      faqAdd: "Add a question",
      back: "All pages",
      seoTitle: "Search title",
      seoDescription: "Search description",
      slugTaken: "Another page uses this address.",
      tabs: { pages: "Pages", blog: "Blog", about: "About", faq: "FAQ", forms: "Forms" },
      cols: { page: "Page", sections: "Sections", status: "Status", updated: "Updated" },
      tiles: {
        pages: "Your pages",
        pagesHint: "Landing, sale, story and more",
        live: "Live on the store",
        liveHint: "Published pages shoppers can open",
        home: "Home page",
        homeHint: "The page shoppers see first",
        themeHome: "Theme home page",
      },
      viewsLabel: "Show pages",
      views: { all: "All", live: "Live", draft: "Not published" },
      menuFor: "More for {name}",
      openEditor: "Open in page editor",
      republish: "Publish changes",
      viewOnStore: "View on store",
      noMatch: "Nothing matches",
      noMatchBody: "Try another word or show everything.",
    },
    blog: {
      new: "New post",
      editTitle: "Edit post",
      search: "Search posts",
      viewsLabel: "Show posts",
      views: { all: "All", published: "Published", scheduled: "Scheduled", draft: "Drafts" },
      states: {
        published: "Published",
        scheduled: "Scheduled",
        draft: "Draft",
        archived: "Archived",
      },
      cols: { post: "Post", author: "Author", state: "Status", when: "Date" },
      tiles: {
        total: "Posts",
        totalHint: "Stories, guides and news",
        live: "Published",
        liveHint: "Shoppers can read them",
        next: "Next to go live",
        nothingScheduled: "Nothing scheduled",
        drafts: "Drafts",
        draftsHint: "Not on the store yet",
      },
      publishedOn: "Published {when}",
      scheduledFor: "Goes live {when}",
      editedWhen: "Edited {when}",
      menuFor: "More for {title}",
      publishNow: "Publish now",
      schedule: "Schedule",
      unpublish: "Unpublish",
      cancelSchedule: "Cancel schedule",
      unpublishTitle: "Take “{title}” off the store?",
      unpublishBody: "It goes back to drafts. You can publish it again any time.",
      unpublished: "“{title}” is back in drafts.",
      publishedToast: "“{title}” is live on your store.",
      scheduledToast: "Scheduled for {when}.",
      savedDraft: "Draft saved.",
      emptyTitle: "No posts yet",
      emptyBody:
        "Share care tips, new arrivals and the story behind your products. Posts bring shoppers from Google.",
      fields: {
        title: "Title",
        excerpt: "Short summary",
        body: "Post",
        slug: "Address",
        author: "Author",
      },
      bodyHelp: "{n} words · about {min} min read. Leave an empty line between paragraphs.",
      goesLive: "Goes live",
      now: "Now",
      later: "Later",
      at: "Date and time",
      pickFuture: "Pick a time in the future.",
      saveDraft: "Save draft",
      saveChanges: "Save changes",
      titleNeeded: "Give the post a title.",
      slugTaken: "Another post uses this address.",
    },
    about: {
      defaultTitle: "About {name}",
      help: "Leave an empty line between paragraphs. Shoppers read this on your About page.",
      preview: "How shoppers see it",
      empty: "Tell shoppers who you are, what you sell and why they can trust you.",
      ideaLabels: { story: "Our story", promise: "Our promise", team: "Our team" },
      ideas: {
        story: "We started in a small shop and now deliver to customers all over Nepal.",
        promise:
          "Every product is checked before it leaves us, and we answer every message the same day.",
        team: "We are a small team of people who love what we sell.",
      },
    },
    faq: {
      count: { one: "{count} question", other: "{count} questions" },
      search: "Search questions",
      removed: "Question removed.",
      removeTitle: "Remove this question?",
      emptyTitle: "No questions yet",
      emptyBody:
        "Answer what shoppers ask most, like delivery time and payment. Start with one of these:",
      ideas: {
        deliveryQ: "How long does delivery take?",
        deliveryA: "2–4 days inside the Kathmandu valley and 4–7 days outside.",
        codQ: "Can I pay cash on delivery?",
        codA: "Yes. Pay the rider in cash when your parcel arrives.",
        returnsQ: "Can I return a product?",
        returnsA: "Yes, within 7 days if it is unused and in its original packing.",
      },
    },
    forms: {
      on: "On",
      received: "Received",
      waiting: "Waiting for a reply",
      openInbox: "Open inbox",
      copyLink: "Copy link",
      spam: "Both forms are protected against spam bots and limited to 10 messages per 15 minutes from one network.",
      contact: {
        title: "Contact form",
        body: "Shoppers ask questions about products, orders and delivery. Messages arrive in Customers → Inbox.",
        fields: "Name, Phone, Email, Order number, Message",
      },
      grievance: {
        title: "Complaint form",
        body: "The complaint form the E-Commerce Act asks for. Each complaint gets a reference number.",
        fields: "Name, Phone, Email, Order number, Complaint",
        deadline:
          "Reply to every complaint within 15 days. The inbox shows the respond-by date and flags late ones.",
      },
    },
    paths: {
      page: "/pages/{slug}",
      blog: "/blog/{slug}",
      pageOption: "{title} · /pages/{slug}",
      crumb: "{store} › pages › {slug}",
      version: "v{v}",
      https: "https://",
      domain: "yourshop.com.np",
      verifyCode: "abc123XYZ…",
      mapUrl: "https://maps.app.goo.gl/…",
      products: "/products",
    },
    code: {
      encoding: "UTF-8",
      lead: "Edit your theme as files: layout, pages and your own CSS. Every save is checked like the visual editor.",
      noTheme:
        "The code editor opens your theme's files. Choose a theme in Theme and editor first.",
      explorer: "Theme files",
      explorerTitle: "Explorer",
      activity: "Editor views",
      commandSearch: "Search files in {store}",
      problems: "Problems",
      preview: "Preview",
      previewTitle: "Code preview",
      previewLive: "Live from the code editor, unsaved changes included",
      previewSaved: "The saved draft. Open the code editor to see changes as you type.",
      spaces: "Spaces: 2",
      findFile: "Find a file",
      folders: { layout: "Layout", pages: "Pages", styles: "Styles", sections: "Section library" },
      insert: "Insert section",
      addSection: "Add section",
      addSectionTo: "Add a section to {file}",
      goToSection: "Go to {section} in the code",
      deleteSection: "Delete {section}",
      format: "Format",
      showPreview: "Show preview",
      hidePreview: "Hide preview",
      visit: "Visit store",
      visual: "Visual editor",
      saveAll: { one: "Save {count} file", other: "Save {count} files" },
      allSaved: "All saved",
      saved: {
        one: "{count} file saved to your draft.",
        other: "{count} files saved to your draft.",
      },
      unsaved: "Unsaved changes",
      savedState: "Saved",
      readOnly: "Read only",
      close: "Close file",
      position: "Ln {line}, Col {col}",
      size: "{n}/{max} characters",
      editorLabel: "File contents",
      previewHome: "Home page preview",
      previewPage: "Preview of {name}",
      fixFirst: "Fix the problem in the highlighted file first.",
      serverField: "Not saved: {path} ({why})",
      jsonAt: "JSON problem near line {line}",
      jsonBad: "This isn't valid JSON",
      cssTooLong: "CSS is limited to {max} characters",
      cssPlaceholder:
        "/* Your own styles for the store. They apply to every page except checkout and payment. */",
      safeNote:
        "Files are checked on save; scripts, outside files and checkout changes are never allowed.",
      css: {
        angle_bracket: "Remove the < character",
        escape: "Backslash escapes aren't allowed",
        at_rule: "@import, @charset and @namespace aren't allowed",
        url: "url() isn't allowed; add photos in the sections instead",
        script: "Script-like code isn't allowed",
        braces: "A { or } is missing",
      },
    },
    migrate: {
      bannerTitle: "Move your store to Adyvance",
      bannerBody:
        "Already selling somewhere else? Connect that store and your products, categories, brands and customers come across in the background.",
      new: "New",
      migrate: "Move my store",
      back: "Back to platforms",
      close: "Close",
      fromTitle: "Move from {name}",
      where: "Where is your shop now? Pick it to bring your catalogue across.",
      direct: "Direct connection",
      soon: "Coming soon",
      again:
        "Each import runs in the background and can be run again later: it only adds what is new.",
      connectTitle: "Connect your {name} store",
      connect: "Connect store",
      checking: "Checking…",
      connected: "Store connected. Choose what to bring across and start the import.",
      checkField: "Check this field.",
      errors: {
        refused:
          "{name} didn't accept these details. Check you pasted the whole key and that it has read access to products, categories, brands and customers.",
        refusedStatus: "{name} didn't accept these details. It answered {status}{said}.",
        chooseStore:
          "This {name} account has more than one store. Type the address of the one to bring across.",
        unavailable: "{name} didn't answer. Try again in a minute.",
        apiKey: "That doesn't look like a {name} API key. Paste the whole key: it starts with zk_.",
        address: "Check the store address.",
      },
      connectedTitle: "Connected store",
      connectedWhen: "connected {when}",
      bring: "What to bring across",
      run: "Start import",
      runAgain: "Import again (only new items)",
      importing: "Importing…",
      started: "Import started. You can leave this page: it carries on.",
      details: "See import details and errors",
      disconnect: "Disconnect",
      disconnectTitle: "Disconnect {name}?",
      disconnectBody:
        "Adyvance forgets this connection and deletes the saved key. Everything already imported stays in your store.",
      disconnected: "Store disconnected.",
      useSheet: "Use a spreadsheet instead",
      whatTitle: "What comes across",
      reading: "Reading… {n} found",
      status: {
        connected: "Connected",
        running: "Importing",
        done: "Imported",
        failed: "Stopped",
      },
      jobState: {
        running: "Importing",
        dry_run: "Getting ready",
        done: "Done",
        failed: "Stopped",
        cancelled: "Cancelled",
      },
      counts: {
        ok: "{n} added",
        skipped: "{n} already here",
        failed: "{n} need fixing",
        photos: "{n} photos",
      },
      kinds: {
        products: "Products",
        productsHelp: "with variants, prices, stock, photos, categories and brands",
        customers: "Customers",
        customersHelp: "customers with a phone number",
        orders: "Past orders",
        ordersHelp: "as history: no stock change, no messages to customers",
      },
      fields: {
        method: "How was your Shopify app made?",
        address: "Store address",
        client_id: "Client ID",
        client_secret: "Client secret",
        access_token: "Admin API access token",
        api_key: "API key",
        blanxerMethod: "How do you want to connect?",
      },
      methods: {
        client: "In the Dev Dashboard",
        clientHint: "Client ID and secret. Every app made since 2026.",
        token: "In the store admin",
        tokenHint: "An shpat_ access token. Apps made before 2026.",
        key: "API key",
        keyHint: "Everything, with hidden products and customers.",
        address: "Store address",
        addressHint: "Published products only. No key needed.",
      },
      sources: {
        shopify: {
          name: "Shopify",
          brings: "Products, categories, brands, customers and past orders",
          lead: "Connect with a private app you make in your own Shopify account. Adyvance only reads from Shopify.",
          addressHelp:
            "The myshopify.com address (Shopify: Settings → Domains), not your own domain.",
          safe: "Adyvance only reads from Shopify and keeps these details encrypted. Disconnect at any time; nothing in your Shopify store changes.",
          guideTitle: "Make the Shopify app",
          steps:
            "With the account that owns the store, open the Shopify Dev Dashboard and choose Apps → Create app. Call it something like “Adyvance import”.|In the app's version, add these Admin API access scopes:|Release that version, then install the app on your store.|In the app's Settings, copy the Client ID and Client secret and paste them here.|The app and the store must be in the same Shopify organization, so make the app with the store owner's account. Have an older app made in the store admin? Choose “In the store admin” and paste its shpat_ token instead (same scopes).",
          scopes:
            "read_products=Products, variants and photos (needed)|read_inventory=Stock, cost per item and weight|read_customers=Customers|read_orders=Orders from the last 60 days|read_all_orders=Older orders (Shopify must approve it for your app)",
          open: "Open the Shopify Dev Dashboard",
          what: "Products with descriptions, status, tags and SEO text|Variants with their own price, sale price, cost per item, SKU, barcode, weight and stock|Photos, copied into your media library|Product type as the category and vendor as the brand|Customers who have a phone number|Past orders with their items, totals and payment status, kept as history: stock doesn't change and customers get no messages",
          not: "Without read_all_orders, only orders from the last 60 days come across. Reviews don't come across yet. Prices come as they are in Shopify: check your Shopify currency is NPR.",
        },
        blanxer: {
          name: "Blanxer",
          brings: "Products, categories, brands and customers",
          lead: "Paste your store's API key. No Blanxer password is needed, and Adyvance only reads with it.",
          leadAddress:
            "Type your Blanxer store's address. No key is needed: Adyvance reads your public catalogue, so only published products come across.",
          addressHelp: "Like yourstore.blanxer.io, or your own domain if the store uses one.",
          addressOptional:
            "Only needed if your Blanxer account has more than one store: the address of the one to bring across.",
          safe: "The key is kept encrypted and only used to read. Nothing in your Blanxer store changes. When the import is done, replace the key in Blanxer (Store Setting → API Key) to switch this one off.",
          safeAddress: "Only your public catalogue is read. Nothing in your Blanxer store changes.",
          guideTitle: "Get the API key",
          steps:
            "Sign in to Blanxer with the account that owns the store.|Open Store Setting → API Key.|Press Create API Key (or use the one already there) and copy it. It starts with sk_.|Paste it here and connect.|No key? Choose “Store address” instead to bring your published products.",
          what: "Categories (made if you don't have them, reused if you do)|Brands|Products with descriptions, prices, sale prices, cost, SKU, barcode, tags and SEO text, hidden ones too|Colour and size variants, each with its own price, stock and photo|Photos, copied into your media library|Customers who have a phone number",
          not: "With the store address instead of the key, only published products come across, without cost prices or customers. Orders don't come across yet.",
        },
        zalient: {
          name: "Zalient",
          brings: "Products, categories, brands and customers",
          lead: "Paste an API key from your Zalient shop. Adyvance only reads with it.",
          addressHelp: "Like yourstore.zalient.shop. The API key below does the reading.",
          safe: "The key is kept encrypted and only used to read. Revoke it in Zalient when the import is done.",
          guideTitle: "Get the API key",
          steps:
            "In your Zalient admin open Settings → Apps → API Keys.|Create a key with read access to products, categories, brands and customers.|Copy the key (it starts with zk_live_ and is shown once) and paste it here.|When the import is done, revoke the key in Zalient.",
          what: "Products with descriptions, status, tags and SEO text|Variants with their own price, sale price, SKU, barcode and stock|Photos, copied into your media library|Each product's category and brand|Customers who have a phone number",
          not: "Orders, reviews, coupons and blog posts don't come across yet.",
        },
        pathao: {
          name: "Pathao Commerce",
          brings: "Products with variants, stock and categories",
          lead: "Type your shop's pathao.shop address. No password is needed: Adyvance reads the products your shop shows online.",
          addressHelp:
            "Like yourshop.pathao.shop. If your shop uses its own domain, type its pathao.shop address instead.",
          safe: "Only what your shop shows online is read. Nothing in your Pathao Commerce shop changes.",
          guideTitle: "Before you start",
          steps:
            "Open your shop in a browser and copy its address, the one ending in pathao.shop.|Make sure the products you want to move are published, so they show in your shop.|Paste the address here and connect.|Start the import. Run it again later to bring only new products.",
          what: "Categories and subcategories (made if you don't have them, reused if you do)|Products with descriptions, prices and sale prices|Variants, each with its own price, stock and photo|Stock levels|Photos, copied into your media library",
          not: "Only published products come across. Customers and orders aren't public on Pathao Commerce, so they don't come across. Prices come as they are: check your shop's currency.",
        },
      },
    },
    move: {
      lead: "Bring your products, stock and customers from another platform in three steps.",
      heroTitle: "Moving to Adyvance is free",
      heroBody:
        "Nothing is saved until you check it. Every row is tested first, and you get a list of any rows that need fixing.",
      step1: "Where is your store now?",
      step2: "Download your products from {name}",
      step3: "Upload them here",
      upload: "Upload products",
      safe: "You match the columns and see a test run before anything is saved.",
      sources: {
        shopify: "Shopify",
        blanxer: "Blanxer",
        zalient: "Zalient",
        pathao: "Pathao Commerce",
        woocommerce: "WooCommerce",
        daraz: "Daraz",
        social: "Facebook or Instagram",
        sheet: "Excel or Google Sheets",
        other: "Another platform",
      },
      how: {
        shopify: {
          a: "In your Shopify admin, open Products and click Export.",
          b: "Pick “All products” and “CSV for Excel”, then Export. Big stores get the file by email.",
          c: "Upload that file here. Variants, prices and photo links are matched for you.",
        },
        woocommerce: {
          a: "In WordPress, open Products → All Products and click Export.",
          b: "Keep all columns and click Generate CSV.",
          c: "Upload that file here. Photo links can be fetched for you.",
        },
        daraz: {
          a: "In Daraz Seller Center, open your products list.",
          b: "Use the export or bulk-edit option to download your products as Excel.",
          c: "Save it as CSV and upload it here.",
        },
        social: {
          a: "Write your products in Excel or Google Sheets: name, price, size or colour, stock.",
          b: "Use one row per size or colour, with the same name on each row.",
          c: "Save as CSV and upload it here. Add photos from your phone afterwards.",
        },
        sheet: {
          a: "Use one row per product, or one per size or colour.",
          b: "Name the columns like Title, Price, SKU and Stock; most other names are understood too.",
          c: "Save as CSV and upload it here.",
        },
        other: {
          a: "Look for Export or Download on the products page of your current platform.",
          b: "Choose CSV or Excel with all columns.",
          c: "Upload it here and match the columns before anything is saved.",
        },
      },
      what: {
        products: "Products",
        productsHelp: "Names, descriptions, sizes and colours, prices and photos.",
        stock: "Stock",
        stockHelp: "How many you have of each size or colour.",
        customers: "Customers",
        customersHelp: "Names and phone numbers, for SMS and repeat orders.",
      },
    },
    seo: {
      lead: "Get every product found on Google, Bing and AI assistants like ChatGPT, Perplexity, Gemini and Claude.",
      tabsLabel: "SEO and AI visibility",
      tabs: {
        overview: "Overview",
        engines: "Search engines",
        ai: "AI assistants",
        products: "Products",
        feeds: "Product feeds",
      },
      unsaved: "Unsaved changes",
      notLive:
        "Your store is not open yet, so search engines and AI assistants can't read it. Everything here starts working the moment you open it.",
      openStore: "Open the store",
      homeTitle: "Your store on Google",
      homeLead:
        "The title and text shown in search results, and the photo shown when your link is shared.",
      title: "Search title",
      description: "Search description",
      descriptionPh: "What you sell and where, in one or two sentences.",
      image: "Share photo (Facebook, WhatsApp, Viber)",
      sharePreview: "When shared",
      verifyTitle: "Google Search Console",
      verifyBody:
        "Prove to Google that the store is yours, send your sitemap, and see which searches bring shoppers.",
      verifyCode: "Verification code",
      verifyHelp: "In Search Console pick “HTML tag” and paste the tag or just its code.",
      codeAdded: "Code added",
      openConsole: "Open Search Console",
      submitSitemap: "Submit your sitemap",
      bingTitle: "Bing Webmaster Tools",
      bingBody:
        "Bing's index also feeds Microsoft Copilot and other AI search tools. Verify the store to see its searches there.",
      bingHelp: "In Bing Webmaster Tools pick “HTML Meta Tag” and paste the tag or just its code.",
      openBing: "Open Bing Webmaster Tools",
      indexnow: {
        title: "Instant indexing (IndexNow)",
        body: "Tell Bing, Yandex, Seznam and Naver about every page at once, instead of waiting for them to find it.",
        send: "Send every page now",
        sending: "Sending…",
        sent: { one: "Sent {count} page to IndexNow.", other: "Sent {count} pages to IndexNow." },
        last: { one: "{count} page sent {when}.", other: "{count} pages sent {when}." },
        needsLive: "Works once your store is open.",
        hint: "Send again after adding many products.",
        google: "Google doesn't use IndexNow; it reads your sitemap, which updates by itself.",
      },
      filesTitle: "Files your store serves",
      filesBody: "Your store makes these by itself and keeps them up to date.",
      automatic: "Automatic",
      files: {
        sitemap: "Sitemap",
        sitemapHelp: "Lists every product, collection and page for search engines.",
        robots: "Robots file",
        robotsHelp: "Keeps cart and checkout pages out of search, and applies your AI choice.",
        llms: "llms.txt",
        llmsHelp: "A plain summary of your store and products for AI assistants.",
      },
      scoreTitle: "Search and AI visibility",
      scoreBody: "What helps shoppers find you on Google and in AI answers.",
      groups: { google: "Search engines", ai: "AI assistants" },
      checks: {
        live: "Store open to shoppers",
        title: "Search title set",
        description: "Description of 50+ characters",
        image: "Share photo added",
        verify: "Google Search Console code",
        bing: "Bing Webmaster Tools code",
        policies: "At least one policy live",
        pages: "Search text on every page ({n} missing)",
        aiOpen: "AI assistants can read the store",
        aiSearch: "AI search crawlers allowed",
        products: "{ready} of {total} products ready for search",
      },
      fix: "Fix",
      autoTitle: "Done for you",
      auto: {
        sitemap: "Sitemap with every product and page",
        robots: "Robots file with your AI choice",
        llms: "llms.txt for AI assistants",
        schema: "Structured data on every page",
        indexnow: "IndexNow key, ready to send",
        canonical: "Canonical links and share tags",
      },
      autoLater: "They go online with your store.",
      tipsTitle: "Tips",
      tips: {
        t1: "Put what you sell and where in the title, like “Handmade dhaka topi and kurta | Kathmandu”.",
        t2: "Give every product its own description. AI assistants quote facts: size, material, origin, care.",
        t3: "Add clear photos with alt text; Google Images and AI shopping answers use them.",
        t4: "Answer real questions on your FAQ page; AI assistants pick up direct answers.",
      },
      aiTitle: "AI assistants",
      aiBody:
        "Choose which AI crawlers may read your store. Search crawlers put your products in AI answers.",
      policy: {
        all: "All AI",
        allHelp: "Show up in AI answers and let AI companies learn from your pages. Most reach.",
        search: "AI answers only",
        searchHelp:
          "Show up in ChatGPT, Claude and Perplexity answers; block crawlers that train models.",
        none: "No AI",
        noneHelp: "Keep every AI crawler out. Google and Bing search still work.",
      },
      recommended: "Recommended",
      aiNote:
        "Each company publishes the name its crawler uses. Saving updates your robots file within an hour.",
      llmsTitle: "Store summary for AI (llms.txt)",
      llmsBody:
        "Your store, products, prices, categories, policies and contact details in plain text that AI assistants read quickly.",
      llmsOff: "Turned off while “No AI” is chosen.",
      llmsLater: "Goes online when your store opens.",
      schemaTitle: "Structured data",
      schemaBody:
        "Machine-readable facts on every page. Google uses them for rich results; AI assistants for exact prices and stock.",
      schema: {
        store: "Online store",
        storeHelp: "Name, logo, legal name, PAN/VAT, contact, social profiles, Nepal.",
        website: "Website",
        websiteHelp: "Your site name in Google, and a search assistants can use.",
        product: "Products",
        productHelp: "Photos, brand, category, price in NPR, stock, condition, seller.",
        listing: "Collections and categories",
        listingHelp: "Each listing's products in order.",
        breadcrumbs: "Breadcrumbs",
        breadcrumbsHelp: "Home › category › product, shown in search results.",
        blog: "Blog posts",
        blogHelp: "Title, author and dates for every post.",
      },
      testRich: "Test in Google Rich Results",
      testSchema: "Check with Schema.org",
      ask: {
        title: "Ask AI about your store",
        body: "See what AI assistants say about your store today.",
        question: "What does {name} ({url}) sell, and what do shoppers say about it?",
        chatgpt: "ChatGPT",
        perplexity: "Perplexity",
        google: "Google AI Mode",
        claude: "Claude",
        note: "New stores take days to weeks to show up in AI answers, after their crawlers visit.",
      },
      productsTitle: "Product search readiness",
      productsBody:
        "Every product on your website, weakest first, with what to add so Google and AI assistants can show it.",
      productsNoAccess: "Only staff who can see products can see this list.",
      audit: {
        ready: "Ready for search",
        work: "Need work",
        average: "Average score",
        view: { work: "Need work", all: "All products" },
        loading: "Checking your products…",
        allGood: "Every product is ready for search.",
        none: "No products on your website yet.",
        checking: "Checking…",
        improve: "Improve",
        opening: "Opening…",
      },
      issues: {
        description: "Short description",
        photo: "No photo",
        morePhotos: "Fewer than 3 photos",
        alt: "Photo alt text",
        brand: "No brand",
        category: "No category",
        seo: "No search title or text",
      },
      merchant: {
        title: "Google Shopping and Merchant Center",
        body: "Free listings put your products in Google Shopping, Images and Google's shopping answers.",
        s1: "Turn on the Google feed above and copy its link.",
        s2: "In Merchant Center open Products → Add products → From a file → Add a file link, and paste it.",
        s3: "Pick a daily update. Google checks the products, usually within a few days.",
        open: "Open Merchant Center",
        meta: "Open Commerce Manager",
      },
      feeds: {
        title: "Product feeds",
        body: "Show your products in Google Shopping and Facebook and Instagram shops. Copy the link into Merchant Center or Commerce Manager.",
        google: "Google Merchant Center",
        googleHelp: "Free listings and Shopping ads.",
        meta: "Facebook and Instagram catalog",
        metaHelp: "Shops on Facebook and Instagram, product ads.",
        on: "Feed switched on.",
        off: "Feed switched off.",
        ownerOnly: "Only staff who manage integrations can switch feeds.",
        note: "Feeds list active products and refresh every hour once your store is open.",
      },
    },
    featured: {
      lead: "The product sections on your home page: what each one shows and how many.",
      saved: "Saved to your theme draft. Publish the theme to show it on your store.",
      noThemeTitle: "Pick a theme first",
      noThemeBody: "Featured sections live on your home page, which comes with your theme.",
      pickTheme: "Choose a theme",
      emptyTitle: "No product sections yet",
      emptyBody: "Add best sellers, new arrivals or products you pick yourself to your home page.",
      editTitle: "Featured section",
      removeTitle: "Remove this section from the home page?",
      fromCollection: "Collection: {slug}",
      picked: { one: "{count} picked product", other: "{count} picked products" },
      upTo: "up to {n}",
      addTitle: "Add a section",
      publishNote: "Changes go to your theme draft; shoppers see them after you publish.",
      openEditor: "Open the theme editor",
      none: {
        on_sale: "No product is on sale right now. Set a compare-at price to put one on sale.",
        products: "No products picked yet. Edit the section to choose them.",
        other: "Your products appear here.",
      },
      headings: {
        best_sellers: "Best sellers",
        latest: "New arrivals",
        on_sale: "On sale",
        products: "Our picks",
        collection: "Collection",
      },
      tiles: {
        sections: "Product sections",
        sectionsHint: "On your home page",
        shown: "Shown",
        shownHint: "Sections shoppers can see",
        publish: "Theme",
        publishHint: "Publish to put changes live",
      },
    },
    info: {
      removed: "Removed.",
      branches: {
        lead: "Your shops and outlets with address, phone and opening hours, shown on your store's Branches page.",
        add: "Add branch",
        edit: "Edit branch",
        name: "Branch name",
        namePh: "Jhamsikhel showroom",
        nameNeeded: "Give the branch a name.",
        address: "Address",
        phone: "Phone",
        hours: "Opening hours",
        hoursIdeas: {
          sunFri: "Sun–Fri, 10 am – 7 pm",
          everyDay: "Every day, 9 am – 8 pm",
          sunThu: "Sun–Thu, 10 am – 6 pm",
        },
        map: "Map link",
        mapHelp: "Open the place in Google Maps, tap Share and paste the link here.",
        mapBad: "Paste a full link that starts with https://",
        openMap: "Open in Maps",
        removeTitle: "Remove {name}?",
        removeBody: "It disappears from your store's Branches page.",
        shownAt: "Shoppers see your branches at",
        emptyTitle: "No branches yet",
        emptyBody: "Add your shop so shoppers can visit, call or find it on the map.",
        tiles: {
          count: "Branches",
          countHint: "Shops and outlets",
          maps: "With a map link",
          mapsHint: "Shoppers get directions in one tap",
          page: "Branches page",
          pageOn: "Live on your store",
          pageOff: "Appears after the first branch",
        },
      },
      points: {
        lead: "Short reasons to buy from you, shown beside the buy button on every product page.",
        add: "Add point",
        edit: "Edit point",
        full: "Up to {n} selling points.",
        icon: "Icon",
        title: "Title",
        body: "Detail (optional)",
        removeTitle: "Remove this selling point?",
        emptyTitle: "No selling points yet",
        emptyBody: "Cash on delivery, easy returns, fast delivery… Add the ones you offer:",
        previewTitle: "On a product page",
        previewNote: "Shoppers see these under the buy button, in this order.",
        sampleProduct: "Dhaka topi",
        sampleBuy: "Add to cart",
        ideas: {
          delivery: "Delivery all over Nepal",
          deliveryBody: "2–4 days inside the valley, 4–7 days outside.",
          cod: "Cash on delivery",
          codBody: "Pay when the parcel reaches you.",
          returns: "7-day easy returns",
          returnsBody: "Not right? Send it back within a week.",
          secure: "Safe online payment",
          secureBody: "eSewa, Khalti and bank QR.",
          support: "Help when you need it",
          supportBody: "Call or message us, we reply the same day.",
          local: "Made in Nepal",
          localBody: "Made by local makers.",
        },
      },
    },
    menus: {
      title: "Menus",
      lead: "The links in your store's header and footer.",
      main: "Header menu",
      footer: "Footer menu",
      label: "Link text",
      href: "Link address",
      hrefHelp: "A page on your store, like /products, or a full https:// link.",
      addLink: "Add a link",
      savedToast: "Menu saved.",
      reset: "Undo changes",
      subLinks: "Sub-links",
      addSubLink: "Add a sub-link",
      full: "A menu holds up to {n} links.",
      pickTitle: "Where does this link go?",
      hrefBad: "Start with / for a page on your store, or https:// for another site.",
      useLink: "Use this link",
      noneHere: "Nothing here yet.",
      previewNote: {
        main: "On phones the header menu opens from the menu button.",
        footer: "Shown at the bottom of every page, next to your policies.",
      },
      groups: {
        store: "Store pages",
        pages: "Your pages",
        collections: "Collections",
        categories: "Categories",
        policies: "Policies",
        custom: "Other link",
      },
      links: {
        home: "Home",
        products: "All products",
        search: "Search",
        track: "Track your order",
        contact: "Contact",
        faq: "Questions and answers",
        branches: "Our branches",
        blog: "Blog",
        account: "My account",
        about: "About us",
      },
    },
    domains: {
      title: "Domains",
      lead: "Where your store can be reached.",
      canonical: "Main address",
      alias: "Preview address",
      redirect: "Redirects to main",
      aliasHelp: "Opens the same store. Search engines don't list it.",
      redirectHelp: "Sends shoppers on to your main address.",
      customTitle: "Your own domain",
      mainHelp: "Share this link on Facebook, Instagram and TikTok. Shoppers open your store here.",
      copy: "Copy link",
      open: "Open store",
      openAddress: "Open {host}",
      others: "Other addresses",
      changeStatus: "Change",
      statusHelp: {
        live: "Shoppers can browse and buy.",
        notLive: "Shoppers see a coming soon page.",
        password: "Shoppers need a password to enter.",
        suspended: "Shoppers see a maintenance page.",
      },
      status: {
        live: "Open",
        coming_soon: "Coming soon",
        password: "Password",
        draft: "Draft",
        suspended: "Paused",
      },
      share: "Share",
      shareWhatsapp: "WhatsApp",
      shareFacebook: "Facebook",
      shareMore: "More",
      shareText: "Shop at {name}: {url}",
      customLead: "Use an address you own, like yourshop.com.np.",
      comingSoon: "Coming soon",
      customNote:
        "You will connect your domain here once it opens. Your Adyvance address keeps working either way.",
      steps: {
        enter: "Enter your domain",
        enterHelp: "The address you already own.",
        records: "Add two DNS records",
        recordsHelp: "We show you exactly what to copy into your domain provider.",
        secure: "We check and secure it",
        secureHelp: "HTTPS is set up for you, usually within an hour.",
      },
      tip: ".com.np domains are free for businesses registered in Nepal:",
    },
    inbox: {
      title: "Inbox",
      lead: "Messages and complaints from your store's contact and complaint forms.",
      empty: "No messages yet.",
      all: "All",
      contact: "Messages",
      grievance: "Complaints",
      due: "Respond by {when}",
      overdue: "Overdue",
      masked: "Contact details are hidden for your role.",
      pipeline: {
        new: "New",
        follow_up: "Following up",
        converted: "Converted",
        resolved: "Resolved",
        spam: "Spam",
      },
      setPipeline: "Status",
      updated: "Updated.",
      loadMore: "Load more",
      reference: "Reference {ref}",
      noName: "No name",
      kindOne: { contact: "Message", grievance: "Complaint" },
      typeLabel: "Message type",
      statusLabel: "Message status",
      views: { open: "Open", all: "All statuses" },
      tiles: {
        new: "New",
        overdue: "Overdue complaints",
        following: "Following up",
        all: "All messages",
      },
      searchLabel: "Search the inbox",
      searchPh: "Name, phone, words or reference",
      noMatchTitle: "Nothing matches",
      noMatchBody: "Try another type, status or search.",
      showAll: "Show everything",
      columns: {
        ref: "Ref.",
        from: "From",
        type: "Type",
        message: "Message",
        status: "Status",
        assignee: "Handled by",
        due: "Respond by",
        received: "Received",
        actions: "Actions",
      },
      close: "Close",
      phone: "Phone",
      email: "Email",
      call: "Call",
      copy: "Copy phone",
      copyPhone: "Copy phone number",
      copied: "Copied",
      replyEmail: "Reply by email",
      replySubject: "Re: your message {ref}",
      fields: { order_number: "Order number" },
      overdueSince: "Overdue since {when}",
      dueHelp:
        "Complaints must get a response by this date. Mark it Resolved once you've answered.",
      markedAs: "Marked as {status}",
      assigned: "Assigned",
      unassigned: "No one is handling it now",
      handledBy: "Handled by {name}",
      nobody: "No one is handling this yet",
      nobodyOption: "No one",
      assignTo: "Handled by",
      assignMe: "Assign to me",
      you: "You",
      someone: "A team member",
      menu: {
        label: "Actions for the message from {name}",
        open: "Open message",
        markAs: "Mark as",
      },
    },
  },
} as const;
